import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import i18n from '@/i18n';
import { auth } from '@/lib/firebase';
import { clearLocalDrafts } from '@/lib/local-drafts';
import { readPendingEmail, setPendingEmail } from './email-link-store';

export type AuthStatus = 'loading' | 'signed-out' | 'signed-in' | 'completing-email-link';

/** Return-leg and request outcome of the email-link path, carried to `LoginPage` (SL-0045
 * Task 2.3): `'email-required'` is the cross-device case — the link was opened in a browser that
 * never stored the address — and has no other route to the UI. */
export type EmailLinkOutcome = 'idle' | 'sent' | 'email-required' | 'failed';

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  emailLinkOutcome: EmailLinkOutcome;
  // Property function types, not method shorthand: a method's implicit `this` would trip
  // @typescript-eslint/unbound-method wherever a consumer destructures these off the context value.
  signInWithGoogleWorkspace: () => Promise<void>;
  signOutCurrentUser: () => Promise<void>;
  requestEmailLink: (email: string) => Promise<void>;
  /** Returns the email-link block to its request form after a `'sent'` outcome — the only way back
   * for a mistyped address short of reloading the page. Never touches `status`: it clears the
   * REQUEST outcome, not a completion in flight. */
  resetEmailLinkRequest: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function signInWithGoogleWorkspace(): Promise<void> {
  const provider = new GoogleAuthProvider();
  const hostedDomain = import.meta.env.VITE_GOOGLE_WORKSPACE_HOSTED_DOMAIN;
  if (hostedDomain) {
    provider.setCustomParameters({ hd: hostedDomain });
  }
  await signInWithPopup(auth, provider);
}

async function signOutCurrentUser(): Promise<void> {
  // The drafts go once the sign-out succeeds: the auth-state callback clears them when the account
  // ends, so a failed sign-out keeps the person signed in with their unsaved forms.
  await signOut(auth);
}

/**
 * Auth-only session state (FR-4.6/FR-6.3): holds only `status` and the identity `user`, never a
 * token or a role/permission. The bearer is fetched fresh per `/v1` request in `api-client.ts`
 * (`auth.currentUser?.getIdToken()`) — the Firebase SDK's own cache/refresh is the only cache.
 *
 * Second sign-in path (SL-0045 Activity 2): passwordless email-link, beside the untouched Google
 * Workspace path. The return leg is completed here in the provider: `status` is set to
 * `'completing-email-link'` SYNCHRONOUSLY at mount when the URL is a sign-in link, because
 * `onAuthStateChanged` fires early with `currentUser === null` and would otherwise paint
 * `'signed-out'` (and `LoginPage`) over a completion already in flight.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(() =>
    isSignInWithEmailLink(auth, window.location.href) ? 'completing-email-link' : 'loading',
  );
  const [user, setUser] = useState<User | null>(null);
  const [emailLinkOutcome, setEmailLinkOutcome] = useState<EmailLinkOutcome>('idle');
  // Synchronous mirror of the completion-in-flight condition: the auth-state callback consults it
  // in the same tick it fires, before any re-render could deliver fresh state.
  const completingRef = useRef(status === 'completing-email-link');
  /** The last signed-in account: when it ends or changes (expiry, another tab), drafts go too. */
  const lastUid = useRef<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      const uid = currentUser?.uid ?? null;
      if (lastUid.current !== null && lastUid.current !== uid) clearLocalDrafts();
      if (uid !== null) lastUid.current = uid;
      setUser(currentUser);
      if (currentUser) {
        completingRef.current = false;
        setStatus('signed-in');
      } else if (!completingRef.current) {
        setStatus('signed-out');
      }
      // A null user while a completion is in flight: stay in 'completing-email-link' — the
      // transition out happens only on a non-null user or on completion failure (Task 2.2).
    });
    return unsubscribe;
  }, []);

  const completeEmailLink = useCallback(async (email: string): Promise<void> => {
    try {
      await signInWithEmailLink(auth, email, window.location.href);
      setPendingEmail(null);
      setEmailLinkOutcome('idle');
      // onAuthStateChanged carries the transition to 'signed-in'.
    } catch {
      completingRef.current = false;
      setEmailLinkOutcome('failed');
      setStatus('signed-out');
    }
  }, []);

  // The return leg (Task 2.5): runs once at mount when the URL is a sign-in link. With a stored
  // address it completes directly; without one (cross-device — the link was opened in a different
  // browser) it surfaces 'email-required' and stays in 'completing-email-link' until the user
  // supplies the address through `requestEmailLink`.
  useEffect(() => {
    if (!completingRef.current) {
      return;
    }
    const pending = readPendingEmail();
    if (pending === null) {
      setEmailLinkOutcome('email-required');
      return;
    }
    void completeEmailLink(pending);
  }, [completeEmailLink]);

  const requestEmailLink = useCallback(
    async (email: string): Promise<void> => {
      // In the 'email-required' return-leg state this same entry point COMPLETES the sign-in with
      // the supplied address instead of sending a new link — Task 3.2's re-prompt "re-invokes
      // completion with it", and the context deliberately gains no second member for that
      // (Task 2.3 fixes the added members to `requestEmailLink` and `emailLinkOutcome`).
      if (
        emailLinkOutcome === 'email-required' &&
        isSignInWithEmailLink(auth, window.location.href)
      ) {
        await completeEmailLink(email);
        return;
      }
      // The built-in sign-in email localizes to the active UI locale (Human exchange 2026-08-08:
      // template bodies are not editable, language is the customization lever that exists).
      auth.languageCode = i18n.language;
      await sendSignInLinkToEmail(auth, email, {
        // `window.location.origin` is an authorized domain per P2; `handleCodeInApp: true` is
        // mandatory or Firebase does not treat the link as in-app sign-in (Task 2.4).
        url: `${window.location.origin}/`,
        handleCodeInApp: true,
      });
      setPendingEmail(email);
      setEmailLinkOutcome('sent');
    },
    [emailLinkOutcome, completeEmailLink],
  );

  const resetEmailLinkRequest = useCallback(() => {
    setEmailLinkOutcome((current) => (current === 'sent' ? 'idle' : current));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        status,
        user,
        emailLinkOutcome,
        signInWithGoogleWorkspace,
        signOutCurrentUser,
        requestEmailLink,
        resetEmailLinkRequest,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
